import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tab, Tabs, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import { supplierService } from '../../services/supplierService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFormatageDates } from '../../utils/dates';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import PastilleStatut from '../commun/PastilleStatut';
import PiedTableau from '../commun/PiedTableau';
import { numeroAffichable } from '../../utils/numeroCommande';

/**
 * Le détail d'un fournisseur — lecture. Le formulaire vit dans la liste.
 *
 * 🔴 LA SÉPARATION EST LE SUJET DU LOT. Avant RU5-e, l'écran n'avait QU'UN
 * dialogue : sept champs de saisie, et sous eux une carte « Dernières commandes »
 * — de la lecture collée sous une saisie. Produits et Commandes ont depuis
 * longtemps détail-en-lecture + « Modifier » qui ouvre le formulaire ; le
 * fournisseur était le dernier à ne pas l'avoir.
 *
 * ⚠️ ET LA CARTE ÉTAIT HORS DE `DialogContent` — sœur du contenu, pas fille :
 * elle échappait à la zone de défilement du dialogue. Le défaut disparaît en
 * déplaçant la lecture ici, pas en corrigeant une balise : *une imbrication
 * fausse est souvent le symptôme d'un contenu qui n'est pas à sa place.*
 *
 * 🔴 « DERNIÈRES COMMANDES » MONTRAIT LES PREMIÈRES. Aucun `order_by` dans la
 * relation, aucun dans la route, un `.slice(0, 5)` ici : mesuré sur un décor où
 * l'ordre d'insertion diffère de l'ordre des dates, **une seule commande commune
 * sur cinq**. L'ordre vit maintenant en SQL, où il survit à la pagination.
 * *Un tri qui vit dans un `slice` n'est pas un tri : c'est une coupe dans un
 * ordre qu'on n'a pas choisi.*
 */
const DialogueDetailFournisseur = ({ id, ouvert, onFermer, onModifier }) => {
  const { t } = useLanguage();
  const { formaterDateSaisie } = useFormatageDates();

  const [fournisseur, setFournisseur] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);

  // ⚠️ UN SEUL COUPLE page/parPage POUR LES DEUX ONGLETS, remis à 1 au
  // changement — même raison qu'au détail produit : deux paginations
  // indépendantes donneraient un pied de tableau qui annonce le total de
  // l'onglet qu'on ne regarde pas.
  const [onglet, setOnglet] = useState('commandes');
  const [page, setPage] = useState(1);
  const [parPage, setParPage] = useState(20);

  const [commandes, setCommandes] = useState([]);
  const [totalCommandes, setTotalCommandes] = useState(0);
  const [pagesCommandes, setPagesCommandes] = useState(1);
  const [erreurCommandes, setErreurCommandes] = useState(false);

  const [produits, setProduits] = useState([]);
  const [totalProduits, setTotalProduits] = useState(0);
  const [pagesProduits, setPagesProduits] = useState(1);
  const [erreurProduits, setErreurProduits] = useState(false);

  const chargerCommandes = useCallback(async () => {
    if (!ouvert || !id) return;
    try {
      const r = await supplierService.getSupplierOrders(id, { page, per_page: parPage });
      setFournisseur(r.supplier || null);
      setCommandes(r.orders || []);
      setTotalCommandes(r.total ?? r.orders_count ?? 0);
      setPagesCommandes(r.pages || 1);
      setEnCours(r.en_cours || null);
      setErreurCommandes(false);
      setErreur(null);
    } catch (e) {
      // ⚠️ 404 ET 403 MÈNENT AU MÊME ÉCRAN : le serveur rend 404 pour une
      // ressource d'une autre entreprise précisément pour ne pas confirmer son
      // existence. Distinguer les deux ici rétablirait l'oracle qu'il refuse.
      if (e?.response?.status === 404) setErreur('introuvable');
      setCommandes([]);
      setErreurCommandes(true);
    } finally {
      setChargement(false);
    }
  }, [id, ouvert, page, parPage]);

  const chargerProduits = useCallback(async () => {
    if (!ouvert || !id) return;
    try {
      const r = await supplierService.getSupplierProducts(id, { page, per_page: parPage });
      setFournisseur(r.supplier || null);
      setProduits(r.products || []);
      setTotalProduits(r.total ?? r.products_count ?? 0);
      setPagesProduits(r.pages || 1);
      setErreurProduits(false);
      setErreur(null);
    } catch (e) {
      if (e?.response?.status === 404) setErreur('introuvable');
      setProduits([]);
      setErreurProduits(true);
    } finally {
      setChargement(false);
    }
  }, [id, ouvert, page, parPage]);

  // ⚠️ ON NE CHARGE QUE L'ONGLET VISIBLE. Charger les deux à l'ouverture
  // doublerait le coût pour une réponse qu'on ne regarde pas.
  // 🔴 MAIS L'ONGLET « COMMANDES » PORTE L'EN-COURS, qui est une information de
  // l'ENTÊTE. Il est donc l'onglet d'ouverture : sans cela, les trois chiffres
  // du haut afficheraient un trou tant qu'on n'a pas cliqué sur un onglet.
  useEffect(() => {
    if (onglet === 'commandes') chargerCommandes(); else chargerProduits();
  }, [onglet, chargerCommandes, chargerProduits]);

  useEffect(() => {
    if (!ouvert) {
      setChargement(true);
      setOnglet('commandes');
      setPage(1);
    }
  }, [ouvert]);

  const coquille = (contenu) => (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="lg" fullWidth>
      {contenu}
    </Dialog>
  );

  if (chargement) {
    return coquille(
      <DialogContent>
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      </DialogContent>,
    );
  }

  if (erreur || !fournisseur) {
    return coquille(
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>{t('suppliers.notFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onFermer}>
          {t('suppliers.backToList')}
        </Button>
      </DialogContent>,
    );
  }

  const devise = fournisseur.currency || 'EUR';
  const reste = enCours?.remaining_amount ?? 0;

  // ── LES TROIS CHIFFRES, ET UN SEUL SE TEINTE ────────────────────────────
  // 🔴 LE TROISIÈME PORTE SA DÉFINITION DANS SON LIBELLÉ. « En-cours à payer »
  // seul rouvrirait à un deuxième endroit la question d'assiette laissée
  // ouverte en Comptabilité — on saurait le montant sans savoir ce qu'il
  // compte. Le libellé dit donc « hors commandes annulées », et le sous-titre
  // dit sur combien de commandes il porte. *Un chiffre dont on ignore
  // l'assiette est un chiffre qu'on ne peut pas contredire.*
  const chiffres = [
    {
      cle: 'produits',
      libelle: t('navigation.products'),
      valeur: fournisseur.products_count ?? 0,
    },
    {
      cle: 'commandes',
      libelle: t('navigation.orders'),
      valeur: fournisseur.orders_count ?? 0,
    },
    {
      cle: 'encours',
      libelle: t('suppliers.outstandingExcludingCancelled'),
      valeur: formatCurrency(reste, enCours?.currency || devise),
      // ⚠️ LA COULEUR EST DÉCLARÉE MÊME QUAND ELLE NE TEINTE PAS : la garde de
      // RU4-c′ vaut ici aussi — toute surface qui porte un fond de statut
      // déclare sa couleur de texte, et un jeton par défaut n'est pas un oubli.
      couleur: reste > 0 ? 'warning.main' : 'text.primary',
      teinte: reste > 0,
      detail: enCours
        ? t('suppliers.outstandingScope', { count: enCours.orders_counted ?? 0 })
        : null,
    },
  ];

  const contact = [
    { cle: 'contact', libelle: t('suppliers.contactName'), valeur: fournisseur.contact_name },
    { cle: 'email', libelle: t('suppliers.email'), valeur: fournisseur.email },
    { cle: 'phone', libelle: t('suppliers.phone'), valeur: fournisseur.phone },
    { cle: 'address', libelle: t('suppliers.address'), valeur: fournisseur.address },
  ].filter((c) => c.valeur);

  const totalOnglet = onglet === 'commandes' ? totalCommandes : totalProduits;

  return coquille(
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
                   alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="span" display="block" noWrap>
              {fournisseur.company_name}
            </Typography>
            {/* La devise est écrite au titre, comme dans la liste : elle décide
                de TOUS les montants du dialogue, et un montant dont on ignore
                la devise n'est pas un montant. */}
            <Typography variant="caption" color="text.secondary">
              {devise} · {getCurrencySymbol(devise)}
            </Typography>
          </Box>
          {/* 🔴 « MODIFIER » FERME LE DÉTAIL ET OUVRE LE FORMULAIRE — il ne
              l'empile pas. Deux dialogues superposés rendraient le retour
              ambigu. Et le formulaire n'est PAS dupliqué ici : il vit dans la
              liste, seule source. */}
          <Button variant="contained" startIcon={<EditIcon />} onClick={onModifier}>
            {t('common.edit')}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* ── LA FICHE DE CONTACT ────────────────────────────────────────────
            ⚠️ LES ABSENCES NE SONT PAS REMPLIES DE « NON RENSEIGNÉ » QUATRE
            FOIS. Une fiche vide se voit mieux qu'une fiche de quatre mentions
            d'absence — même geste qu'au tableau, où le contact vide ne répète
            pas trois fois la même absence. */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {contact.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('suppliers.noContactDetails')}
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 2,
                       gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              {contact.map((c) => (
                <Box key={c.cle} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {c.libelle}
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {c.valeur}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        {/* ── LES TROIS CHIFFRES ─────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gap: 2, mb: 3,
                   gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {chiffres.map((c) => (
            <Paper
              key={c.cle}
              variant="outlined"
              sx={{
                p: 2,
                ...(c.teinte ? {
                  // Même mécanique que la ligne teintée de RU3-a : `alpha()` sur
                  // un jeton, jamais une couleur figée.
                  backgroundColor: (theme) => alpha(theme.palette.warning.main,
                                                    theme.palette.mode === 'dark' ? 0.13 : 0.13),
                } : {}),
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {c.libelle}
              </Typography>
              <Typography variant="h5" fontWeight={700}
                          sx={{ color: c.couleur || 'text.primary' }}>
                {c.valeur}
              </Typography>
              {c.detail && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {c.detail}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

        {/* ⚠️ DEUX ONGLETS, PAS DEUX SECTIONS EMPILÉES. Ce sont deux questions
            différentes — « que lui achète-t-on » et « que lui a-t-on commandé » —
            et on n'en pose qu'une à la fois. Empilées, la seconde serait sous un
            tableau paginé, donc invisible. */}
        <Tabs
          value={onglet}
          onChange={(e, v) => { setOnglet(v); setPage(1); }}
          sx={{ mb: 2 }}
        >
          <Tab value="commandes" label={t('navigation.orders')} />
          <Tab value="produits" label={t('navigation.products')} />
        </Tabs>

        {onglet === 'commandes' ? (
          <>
            {erreurCommandes && (
              <Alert severity="warning" sx={{ mb: 2 }}>{t('suppliers.ordersError')}</Alert>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('orders.orderNumber')}</TableCell>
                    <TableCell>{t('orders.status')}</TableCell>
                    <TableCell align="right">{t('orders.total')}</TableCell>
                    <TableCell align="right">{t('orders.remainingAmount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commandes.length === 0 && !erreurCommandes && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('suppliers.noRecentOrders')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {commandes.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>
                        {/* Le numéro et la date empilés — même geste qu'au
                            tableau des commandes, où la date a rejoint le
                            numéro : deux faces d'une même identité. */}
                        <Typography variant="body2" fontWeight={600}>
                          {numeroAffichable(o.order_number) || `#${o.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formaterDateSaisie(o.order_date)}
                        </Typography>
                      </TableCell>
                      <TableCell><PastilleStatut statut={o.status} /></TableCell>
                      <TableCell align="right">
                        {formatCurrency(o.total_payable ?? o.total_final ?? 0,
                                        o.currency || devise)}
                      </TableCell>
                      {/* 🔴 LE RESTE DÛ VIENT DU SERVEUR, il n'est pas recalculé
                          ici. Une seconde écriture de la règle finirait par
                          diverger de la première — c'est ce qui vient d'être
                          corrigé en extrayant `reste_du_sql` de la Comptabilité
                          plutôt qu'en la recopiant.
                          🔴 ET UN TIRET SUR LES ANNULÉES. La colonne affichait
                          leur total en ambre pendant que la carte du haut les
                          EXCLUAIT : le même écran comptait deux choses à deux
                          endroits, et c'est la colonne qui avait tort. Le tiret
                          vient de `remaining_amount === null` — la décision est
                          au serveur, l'écran ne fait que la rendre. *Un écran
                          qui doit connaître la règle pour afficher juste finira
                          par en connaître une autre.* */}
                      <TableCell align="right"
                                 sx={{ color: (o.remaining_amount ?? 0) > 0
                                   ? 'warning.main' : 'text.secondary' }}>
                        {o.remaining_amount == null
                          ? '—'
                          : formatCurrency(o.remaining_amount, o.currency || devise)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            {erreurProduits && (
              <Alert severity="warning" sx={{ mb: 2 }}>{t('suppliers.productsError')}</Alert>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('products.name')}</TableCell>
                    <TableCell align="right">{t('products.currentStock')}</TableCell>
                    <TableCell align="right">{t('common.minStock')}</TableCell>
                    <TableCell align="right">{t('products.unitPrice')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {produits.length === 0 && !erreurProduits && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t('suppliers.noProducts')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {produits.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Typography variant="body2">{p.name}</Typography>
                        {/* ⚠️ `is_low_stock` EST LU, JAMAIS RECALCULÉ : c'est la
                            décision 10. Le serveur porte la clause
                            `min_stock > 0` qu'une recopie oubliait à trois
                            endroits. */}
                        {p.is_low_stock && (
                          <Chip size="small" variant="outlined" color="warning"
                                label={t('products.lowStock')} sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell align="right"
                                 sx={{ color: p.is_low_stock
                                   ? (p.current_stock === 0 ? 'error.main' : 'warning.main')
                                   : 'text.primary' }}>
                        {p.current_stock}
                      </TableCell>
                      <TableCell align="right">{p.min_stock}</TableCell>
                      <TableCell align="right">
                        {p.price != null
                          ? formatCurrency(p.price, p.currency || devise) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <PiedTableau
          total={totalOnglet}
          page={page}
          pages={onglet === 'commandes' ? pagesCommandes : pagesProduits}
          parPage={parPage}
          onPage={setPage}
          onParPage={(n) => { setParPage(n); setPage(1); }}
          libelleTotal={`${totalOnglet} `
            + `${(onglet === 'commandes' ? t('navigation.orders') : t('navigation.products')).toLowerCase()}`}
          libelleParPage={t('common.perPage')}
        />
      </DialogContent>
    </>,
  );
};

export default DialogueDetailFournisseur;
